import { useRef, useState, useEffect, useCallback } from 'react';

import {
  initializeFCM,
  isFCMSupported,
  removeAllListeners,
  checkPermissionStatus,
  addTokenRefreshListener,
  addNotificationActionListener,
  addNotificationReceivedListener,
  requestPermissions as requestNativePermissions,
} from 'src/utils/fcm-manager';

import {
  linkDeviceToUser,
  getStoredFCMToken,
  getStoredDeviceId,
  registerDeviceToken,
  clearStoredFCMToken,
  clearStoredDeviceId,
  storeFCMTokenLocally,
  storeDeviceIdLocally,
  deactivateDeviceToken,
} from 'src/actions/fcm-token';

type PermissionStatus = 'granted' | 'denied' | 'prompt';

type UseFCMOptions = {
  onNotificationReceived?: (notification: any) => void;
  onNotificationActionPerformed?: (notification: any) => void;
};

export function useFCM(options: UseFCMOptions = {}) {
  const { onNotificationReceived, onNotificationActionPerformed } = options;

  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>('prompt');
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const listenersRef = useRef<Array<{ remove: () => void }>>([]);

  const isSupported = isFCMSupported();

  // Check permission on mount
  const checkPermission = useCallback(async () => {
    if (!isSupported) return;
    try {
      const status = await checkPermissionStatus();
      setPermissionStatus(status);
    } catch (err) {
      console.error('Error checking FCM permission:', err);
    }
  }, [isSupported]);

  // Request permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false;
    try {
      const status = await requestNativePermissions();
      setPermissionStatus(status);
      return status === 'granted';
    } catch (err) {
      console.error('Error requesting FCM permission:', err);
      return false;
    }
  }, [isSupported]);

  // Initialize FCM - get token, register with backend
  const initialize = useCallback(async () => {
    if (!isSupported || isInitializing) return;

    try {
      setIsInitializing(true);
      setError(null);

      const result = await initializeFCM();
      if (!result) {
        setIsInitializing(false);
        return;
      }

      const { token, platform, deviceId } = result;
      setFcmToken(token);

      // Register token with backend
      await registerDeviceToken(token, platform, deviceId);

      // Store locally for later use
      storeFCMTokenLocally(token);
      storeDeviceIdLocally(deviceId);
      setIsRegistered(true);

      // Try to link device to user (may fail with 401 for guests - that's OK)
      await linkDeviceToUser(deviceId).catch(() => {});
    } catch (err) {
      console.error('Error initializing FCM:', err);
      setError(err instanceof Error ? err : new Error('FCM initialization failed'));
    } finally {
      setIsInitializing(false);
    }
  }, [isSupported, isInitializing]);

  // Link device to authenticated user (after login)
  const linkDevice = useCallback(async () => {
    const deviceId = getStoredDeviceId();
    if (!deviceId) return;

    try {
      await linkDeviceToUser(deviceId);
    } catch (err) {
      console.error('Error linking device:', err);
    }
  }, []);

  // Deactivate token (on logout)
  const deactivateToken = useCallback(async () => {
    const token = getStoredFCMToken();
    const deviceId = getStoredDeviceId();

    if (token) {
      await deactivateDeviceToken(token, deviceId || undefined).catch(() => {});
    }

    setFcmToken(null);
    setIsRegistered(false);
    clearStoredFCMToken();
    clearStoredDeviceId();
  }, []);

  // Setup listeners
  const setupListeners = useCallback(() => {
    if (!isSupported) return;

    // Foreground notification listener
    const receivedListener = addNotificationReceivedListener((notification) => {
      onNotificationReceived?.(notification);
    });
    listenersRef.current.push(receivedListener);

    // Notification tap listener
    const actionListener = addNotificationActionListener((notification) => {
      onNotificationActionPerformed?.(notification);
    });
    listenersRef.current.push(actionListener);

    // Token refresh listener
    const tokenListener = addTokenRefreshListener(async (event) => {
      const newToken = event.token;
      setFcmToken(newToken);
      storeFCMTokenLocally(newToken);

      // Re-register with backend
      const deviceId = getStoredDeviceId();
      if (deviceId) {
        const { getPlatformName } = await import('src/utils/fcm-manager');
        const platform = await getPlatformName();
        await registerDeviceToken(newToken, platform, deviceId).catch(() => {});
      }
    });
    listenersRef.current.push(tokenListener);
  }, [isSupported, onNotificationReceived, onNotificationActionPerformed]);

  // Check permission on mount
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  // Setup listeners on mount
  useEffect(() => {
    setupListeners();

    return () => {
      // Cleanup listeners
      for (const listener of listenersRef.current) {
        listener.remove();
      }
      listenersRef.current = [];
      removeAllListeners();
    };
  }, [setupListeners]);

  return {
    permissionStatus,
    fcmToken,
    isInitializing,
    isRegistered,
    error,
    isSupported,
    requestPermission,
    initialize,
    deactivateToken,
    checkPermission,
    linkDevice,
  };
}
